import React, { useCallback, useEffect, useState } from "react";
import { TrendingUp, Award, Zap, Users, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";

const monthName = (s) => {
  if (!s) return "";
  const [y, m] = s.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
};
const shortMonth = (s) => {
  const [y, m] = s.split("-");
  return new Date(parseInt(y), parseInt(m) - 1).toLocaleString("en-US", { month: "short" });
};

function HistoryChart({ data, scopeLabel }) {
  const max = Math.max(1, ...data.map((d) => d.earnings));
  const total = data.reduce((s, d) => s + d.earnings, 0);
  const avg = data.length ? Math.round(total / data.length) : 0;
  const best = data.reduce((b, d) => (!b || d.earnings > b.earnings ? d : b), null);

  return (
    <div className="rounded-2xl border bg-white p-5 mt-5" style={{ borderColor: "#E8E8E8" }}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-extrabold">Earnings — Last 12 Months</h3>
          <p className="text-[11px] italic mt-0.5" style={{ color: "#8A8A8A" }}>{scopeLabel}</p>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-black tnum" style={{ color: "#D62828" }}>${total.toLocaleString()}</div>
          <div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>TOTAL EARNED</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 my-3 py-3 border-t border-b" style={{ borderColor: "#E8E8E8" }}>
        <div className="text-center">
          <div className="font-extrabold tnum">${avg.toLocaleString()}</div>
          <div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>MONTHLY AVG</div>
        </div>
        <div className="text-center">
          <div className="font-extrabold tnum">{best ? `$${best.earnings.toLocaleString()}` : "$0"}</div>
          <div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>{best ? `BEST (${shortMonth(best.month)})` : "BEST"}</div>
        </div>
      </div>
      <div className="flex items-end gap-1.5 h-44">
        {data.map((d, i) => {
          const h = d.earnings === 0 ? 4 : Math.max(8, Math.round((d.earnings / max) * 130));
          const isCurrent = i === data.length - 1;
          return (
            <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="text-[9px] font-bold tnum mb-1" style={{ color: "#5A5A5A", minHeight: "12px" }}>
                {d.earnings === 0 ? "" : `$${d.earnings >= 1000 ? `${(d.earnings / 1000).toFixed(1)}k` : d.earnings}`}
              </div>
              <div className="w-full rounded-t-md" style={{ height: h, background: isCurrent ? "#2E78C5" : "#D62828", opacity: d.earnings === 0 ? 0.25 : 1 }} />
              <div className={`text-[9px] font-bold mt-1 ${isCurrent ? "tnum" : ""}`} style={{ color: isCurrent ? "#2E78C5" : "#5A5A5A", fontWeight: isCurrent ? 900 : 700 }}>{shortMonth(d.month)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Earnings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAgent = user?.role === "agent";
  const isManager = isAdmin || isAgent;
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [history, setHistory] = useState([]);
  const [scope, setScope] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isManager) {
        const q = isAdmin ? `?scope=${scope}` : "";
        const [b, h] = await Promise.all([
          api(`/compensation/leaderboard${q}`),
          api(`/compensation/history?months=12`),
        ]);
        setBoard(b); setHistory(h.series);
      } else {
        const [c, h] = await Promise.all([
          api(`/compensation/me`),
          api(`/compensation/history?months=12`),
        ]);
        setData(c); setHistory(h.series);
      }
    } catch {} finally { setLoading(false); }
  }, [isManager, isAdmin, scope]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <AppShell><div className="grid place-items-center py-24"><Loader2 className="animate-spin" color="#D62828" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-5xl">
        <span className="eyebrow">{isAdmin ? "MASTER VIEW" : isAgent ? "TEAM VIEW" : "THIS MONTH"}</span>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">{isManager ? (isAdmin && scope === "mine" ? "My Partners" : "Partner Leaderboard") : "Your Earnings"}</h1>
        <p className="text-sm font-extrabold mt-1" style={{ color: "#5A5A5A" }}>{monthName((data?.month || board?.month) ?? "")}</p>

        {isAdmin && (
          <div className="flex gap-2 mt-3">
            <button onClick={() => setScope("all")} className={`chip ${scope === "all" ? "chip-active" : "chip-outline"}`}>Master</button>
            <button onClick={() => setScope("mine")} className={`chip ${scope === "mine" ? "chip-active" : "chip-outline"}`}>My Partners</button>
          </div>
        )}

        {!isManager && data && (
          <>
            <div className="rounded-3xl p-7 mt-6" style={{ background: "#D62828" }}>
              <div className="text-[11px] font-extrabold tracking-[2px]" style={{ color: "rgba(255,255,255,0.85)" }}>EARNED THIS MONTH</div>
              <div className="font-display text-white text-5xl font-black tnum mt-2 tracking-tighter">${data.totals.earnings.toLocaleString()}</div>
              <div className="text-white/85 text-sm font-semibold mt-1">From {data.totals.paid_count} paid case{data.totals.paid_count === 1 ? "" : "s"}</div>
              {data.totals.pipeline_earnings > 0 && (
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/20 text-white/95 text-sm font-semibold">
                  <TrendingUp size={14} color="#E3F0FB" />
                  +${data.totals.pipeline_earnings.toLocaleString()} pending ({data.totals.placed_count} placed, awaiting payment)
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-5">
              {[1, 2, 3].map((t) => {
                const ts = data.tiers[String(t)]; if (!ts) return null;
                return (
                  <div key={t} className={`rounded-2xl border-2 bg-white p-5`} style={{ borderColor: ts.paid_count > 0 ? "#D62828" : "#E8E8E8" }}>
                    <div className="flex items-center justify-between">
                      <span className="font-extrabold text-sm">{ts.label}</span>
                      <span className="font-display font-black" style={{ color: "#D62828" }}>${ts.current_rate}/case</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <div><div className="font-display text-2xl font-black tnum">{ts.paid_count}</div><div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>PAID</div></div>
                      <div><div className="font-display text-2xl font-black tnum">{ts.placed_count}</div><div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>PLACED</div></div>
                      <div><div className="font-display text-2xl font-black tnum" style={{ color: "#D62828" }}>${ts.earnings}</div><div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#8A8A8A" }}>EARNED</div></div>
                    </div>
                    {!ts.is_at_max && ts.next_threshold_in && ts.next_rate && (
                      <div className="flex items-center gap-2 pt-3 mt-3 border-t" style={{ borderColor: "#E8E8E8" }}>
                        <Zap size={14} color="#D62828" />
                        <span className="text-xs" style={{ color: "#5A5A5A" }}>
                          Send <strong>{ts.next_threshold_in} more</strong> to unlock <strong>${ts.next_rate}/case</strong>
                        </span>
                      </div>
                    )}
                    {ts.is_at_max && ts.paid_count > 0 && (
                      <div className="flex items-center gap-2 pt-3 mt-3 border-t" style={{ borderColor: "#E8E8E8" }}>
                        <Award size={14} color="#2E7D32" />
                        <span className="text-xs font-extrabold" style={{ color: "#2E7D32" }}>Top tier rate unlocked!</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="rounded-2xl p-5 mt-5" style={{ background: "#E3F0FB" }}>
              <h3 className="font-extrabold" style={{ color: "#D62828" }}>How payouts scale</h3>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "#5A5A5A" }}>
                Volume is measured in issued and paid cases per calendar month. Bigger volume = bigger pay-per-case. Tier 1 climbs all the way to a 40% commission share for top partners. Tier 3 caps at $100/case.
              </p>
            </div>

            {history.length > 0 && <HistoryChart data={history} scopeLabel="Your monthly earnings trend" />}
          </>
        )}

        {isManager && board && (
          <>
            {history.length > 0 && (
              <HistoryChart data={history} scopeLabel={isAdmin && scope === "mine" ? "Your direct partners only" : isAdmin ? "All agents + partners (master view)" : "Your team only"} />
            )}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="rounded-2xl p-5" style={{ background: "#D62828" }}>
                <div className="text-[10px] font-extrabold tracking-wider" style={{ color: "rgba(255,255,255,0.85)" }}>TOTAL PAYOUT</div>
                <div className="font-display text-3xl font-black text-white tnum mt-2">${board.rows.reduce((a, r) => a + r.earnings, 0).toLocaleString()}</div>
              </div>
              <div className="rounded-2xl p-5" style={{ background: "#2E78C5" }}>
                <div className="text-[10px] font-extrabold tracking-wider" style={{ color: "rgba(255,255,255,0.85)" }}>PARTNERS PAID</div>
                <div className="font-display text-3xl font-black text-white tnum mt-2">{board.rows.filter((r) => r.paid_count > 0).length}</div>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              {board.rows.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={36} color="#8A8A8A" className="mx-auto" />
                  <p className="mt-3" style={{ color: "#8A8A8A" }}>No partners registered yet.</p>
                </div>
              ) : board.rows.map((r, i) => (
                <div key={r.partner_id} className="flex items-center gap-4 bg-white p-4 rounded-2xl border" style={{ borderColor: "#E8E8E8" }}>
                  <div className="w-10 h-10 rounded-full grid place-items-center font-display font-black" style={{ background: "#E3F0FB", color: "#D62828" }}>#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold truncate">{r.partner_name}</div>
                    {r.agency && <div className="text-[11px] italic" style={{ color: "#5A5A5A" }}>{r.agency}</div>}
                    <div className="flex gap-1.5 mt-1.5">
                      <span className="px-2 py-0.5 rounded text-[10px] font-extrabold" style={{ background: "#F5F5F5", color: "#5A5A5A" }}>T1: {r.by_tier["1"] || 0}</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-extrabold" style={{ background: "#F5F5F5", color: "#5A5A5A" }}>T2: {r.by_tier["2"] || 0}</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-extrabold" style={{ background: "#F5F5F5", color: "#5A5A5A" }}>T3: {r.by_tier["3"] || 0}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl font-black tnum" style={{ color: "#D62828" }}>${r.earnings.toLocaleString()}</div>
                    <div className="text-[11px] font-bold" style={{ color: "#5A5A5A" }}>{r.paid_count} paid</div>
                    {r.pipeline_earnings > 0 && <div className="text-[10px] font-bold" style={{ color: "#2E78C5" }}>+${r.pipeline_earnings.toLocaleString()} pending</div>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
